/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    v-if="basePageLoaded"
    class="row"
  >
    <div
      class="col-sm-6 col-sm-offset-3"
      aria-labelledby="accountSettingsPage"
    >
      <h1 id="accountSettingsPage">
        Account Settings Page
      </h1>
      <form @submit.prevent="updateInfo">
        <section aria-labelledby="loginInfo">
          <h2 id="loginInfo">
            Login Information
          </h2>
          <div
            v-if="!changingEmail"
            style="margin-bottom: 12px;"
            aria-labelledby="loginInfo"
          >
            <a
              style="cursor: pointer;"
              tabindex="0"
              @click="setChangingEmail"
            >
              <svg
                class="icon-pencil"
                style="height: 12px; width: 12px;"
              >
                <use xlink:href="/static/svg-symbols.svg#icon-pencil" />
              </svg>
              Change Your Email Address ({{ formValues.oldEmail }})
            </a><br>
          </div>
          <div v-if="changingEmail">
            <p class="help-block">
              To change your email address, you must also re-enter your current password
              for verification purposes.
            </p>
            <div class="form-group">
              <label
                class="control-label"
                for="newEmail"
              >
                New email address
              </label>
              <input
                id="newEmail"
                v-model.trim="formValues.newEmail"
                type="email"
                class="form-control"
                placeholder="email"
                name="newEmail"
              >
              <input
                id="oldEmail"
                v-model.trim="formValues.oldEmail"
                type="hidden"
                class="form-control"
                name="oldEmail"
              >
            </div>
            <div class="form-group">
              <label
                for="password"
                class="control-label"
              >
                Current password
              </label>
              <input
                id="password"
                v-model.trim="formValues.password"
                type="password"
                class="form-control"
                placeholder="password"
              >
            </div>
          </div>
          <p class="help-block">
            To change your password, please
            <router-link :to="{ name: 'password-forgot', query: { email: formValues.oldEmail } }">
              initiate a password reset
            </router-link>
          </p>
        </section>
        <br>
        <section
          v-if="isSpeaker && !isGroupManager"
          aria-labelledby="subscriptionInformation"
        >
          <h2 id="subscriptionInformation">
            Subscription Information
          </h2>
          <div aria-labelledby="subscriptionInformation">
            <router-link
              :to="{ name: 'subscription' }"
            >
              Click to View Your Subscription Details
            </router-link>
          </div>
          <br>
        </section>
        <br>
        <section
          v-if="showEmailNotifications"
          aria-labelledby="emailNotifications"
        >
          <h2 id="emailNotifications">
            Email Notifications
          </h2>
          <div aria-labelledby="emailNotifications">
            Did you know that
            you can configure VoiceVibes to send you an email with your recording results,
            to notify you when recordings are shared with you, or to alert you when someone
            has left you feedback?<br><br>
            <router-link
              :to="{ name: 'email-notifications' }"
            >
              Click to Configure Email Notifications
            </router-link>
          </div>
          <br>
        </section>
        <section aria-labelledby="aBitAboutYa">
          <h2 id="aBitAboutYa">
            A little about you
          </h2>
          <div
            class="form-group"
            :class="{ 'has-error': formValues.firstName && formValues.firstName.length<2 }"
          >
            <label
              for="firstName"
              class="control-label"
            >
              First name
            </label>
            <input
              id="firstName"
              v-model.trim="formValues.firstName"
              type="text"
              class="form-control"
              placeholder=""
              name="first"
              required
              maxlength="50"
              @keyup="autoGenerateName"
            >
          </div>
          <div
            class="form-group"
            :class="{ 'has-error': formValues.lastName && formValues.lastName.length<2 }"
          >
            <label
              for="lastName"
              class="control-label"
            >
              Last name
            </label>
            <input
              id="lastName"
              v-model.trim="formValues.lastName"
              type="text"
              class="form-control"
              placeholder=""
              name="last"
              required
              maxlength="50"
              @keyup="autoGenerateName"
            >
          </div>
          <div
            class="form-group"
            :class="{ 'has-error': formValues.name.length>1 && formValues.name.length<5 }"
          >
            <label
              for="name"
              class="control-label"
            >
              Full name
            </label>
            <input
              id="name"
              v-model.trim="formValues.name"
              type="text"
              class="form-control"
              placeholder=""
              required
              maxlength="101"
              @keypress="disableAutoname"
            >
            <p
              v-if="autoName"
              class="help-block"
            >
              This is auto-generated, but you can edit it if you need to
            </p>
          </div>
          <div class="form-group">
            <label
              for="title"
              class="control-label"
            >
              Title/Position
            </label>
            <select
              id="title"
              v-model="formValues.metadata.title"
              class="form-control"
              name="title"
              required
            >
              <option value="">
                Select...
              </option>
              <option value="college-student">
                College Student
              </option>
              <option value="hs-student">
                High School Student
              </option>
              <option value="professor">
                Professor/Instructor
              </option>
              <option value="k12-teacher">
                K-12 Teacher
              </option>
              <option value="dept-head">
                Department Head
              </option>
              <option value="school-admin">
                School Administrator
              </option>
              <option value="coach">
                Corporate Trainer/Coach
              </option>
              <option value="manager">
                Manager
              </option>
              <option value="director-executive">
                Director/Executive
              </option>
              <option value="professional">
                Professional
              </option>
              <option value="other">
                Other/No Title
              </option>
            </select>
          </div>
        </section>
        <br>
        <section aria-labelledby="moreAboutYa">
          <h2 id="moreAboutYa">
            More about you (optional info to help us improve our algorithms)
          </h2>
          <div class="form-group">
            <label
              for="signUpBirthyear"
              class="control-label"
            >
              Birth year
            </label>
            <select
              id="signUpBirthyear"
              v-model.number="formValues.metadata.birthyear"
              class="form-control"
              name="birthyear"
            >
              <option :value="undefined">
                Select...
              </option>
              <option
                v-for="n in 99"
                v-if="n >= 13"
                :value="new Date().getFullYear()-n"
              >
                {{ new Date().getFullYear()-n }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label
              for="gender"
              class="control-label"
            >
              Gender
            </label>
            <select
              id="gender"
              v-model="formValues.metadata.gender"
              class="form-control"
              name="gender"
            >
              <option :value="undefined">
                Select...
              </option>
              <option value="f">
                Female
              </option>
              <option value="m">
                Male
              </option>
              <option value="o">
                Other
              </option>
            </select>
            <br>
          </div>
        </section>

        <div
          v-if="formValues.isEu === '1'"
          class="form-group"
          aria-labelledby="consentToStore"
        >
          <h2 id="consentToStore">
            Consent to Store and Use Your Information
          </h2>
          As a resident of the European Union, you have certain rights to control how
          your data is stored and used. Please refer to the "ADDITIONAL INFORMATION FOR EU, UK AND SWITZERLAND BASED USERS"
          section in our
          <a
            href="https://www.myvoicevibes.com/privacy-policy.html"
            target="_blank"
          >Privacy Policy</a> for details.
          Additional info located in <a
            href="http://www.myvoicevibes.com/terms.html"
            target="_blank"
          >Terms of Use</a>.
          <div class="form-group">
            <div class="checkbox">
              <label
                for="dataRetention"
                class="control-label lower"
              >
                <input
                  id="dataRetention"
                  v-model="formValues.dataRetention"
                  type="checkbox"
                >
                I agree to allow my personal information and any audio recordings in my VoiceVibes account
                to be stored for a period of up to 5 years after my account has closed.  This allows your
                data to remain with VoiceVibes and enables you to reopen your account after it expires
                and in order to help us to provide you with an improved quality of service.
              </label>
            </div>
          </div>
        </div>
        <div
          v-else
          class="form-group"
        >
          <div class="checkbox">
            Click to view our
            <a
              href="http://www.myvoicevibes.com/terms.html"
              target="_blank"
            >Terms of Use</a> and
            <a
              href="https://www.myvoicevibes.com/privacy-policy.html"
              target="_blank"
            >Privacy Policy</a>.
          </div>
        </div>

        <loading-button :loading="saving">
          Save
        </loading-button>
        <br><br>
      </form>
    </div>
  </div>
</template>

<script>
import BasePage from './BasePage';

export default {
  mixins: [BasePage],
  data() {
    return {
      signingUp: false,
      checkingInvite: false,
      autoName: true,
      saving: false,
      changingEmail: false,
    };
  },
  computed: {
    formValues() {
      let user = this.$store.getters.user;
      if (!user) {
        user = {
          firstName: '',
          lastName: '',
          name: '',
          oldEmail: '',
          metadata: {
            birthyear: undefined,
            gender: undefined,
            title: ''
          },
          isEu: '0',
          dataRetention: false,
        };
      }

      return {
        oldEmail: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        name: user.name,
        metadata: this._.clone(user.metadata), // don't mutate state
        isEu: user.isEu,
        dataRetention: user.dataRetention,
      };
    }
  },
  created() {
  },
  methods: {
    disableAutoname(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      this.autoName = false;
    },
    setChangingEmail() {
      this.changingEmail = true;
      this.$nextTick(() => document.getElementById('newEmail').focus());
    },
    updateInfo() {
      if (this.formValues.newEmail === this.formValues.oldEmail || !this.formValues.newEmail) {
        delete this.formValues.newEmail;
      } else if (!window.confirm(`Are you sure you want to change your login to ${this.formValues.newEmail}?`)) {
        return true;
      }
      this.saving = true;

      this.$store.dispatch('updateUser', this.formValues)
        .then(() => {
          // Vue doesn't auto-update the nav link for whatever reason
          document.getElementById('userProfileLink').innerHTML = this.formValues.name;
          this.$store.dispatch('notify', {
            type: 'success',
            subject: 'Profile Updated',
            text: 'Your user profile info has been saved successfully',
          });
        })
        .catch(errors => {
          const text = (errors && errors[0] && errors[0].message) ?
            'Your user profile info could not be saved due to the following error: ' + errors[0].message :
            'Your user profile info could not be saved. Please correct any invalid info and retry your request.';

          this.$store.dispatch('notify', {
            type: 'error',
            subject: 'Error Saving Profile',
            text,
          });
        })
        .finally(() => {
          this.saving = false;
          this.changingEmail = false;
          this.formValues.newEmail = null;
        });
    },
    autoGenerateName() {
      if (this.autoName) {
        this.formValues.name = this.formValues.firstName + ' ' + this.formValues.lastName;
      }
    },
  }
};
</script>
